import {
  type InsightsPanel,
  type InsightsPanels,
  type PanelRow,
} from './panels'
import { type InsightsPeriod } from './periods'

/** The panel's rows as delivered, or [] — a missing panel is a state, not a crash. */
export const rawPanel = (
  panels: InsightsPanels,
  name: InsightsPanel
): PanelRow[] => panels[name] ?? []

/**
 * Detection is by column rather than by a list of names on purpose: a new
 * windowed panel then works without anyone registering it, and a panel that
 * loses its period column degrades to showing everything rather than nothing.
 */
export const isWindowed = (
  panels: InsightsPanels,
  name: InsightsPanel
): boolean => {
  const rows = rawPanel(panels, name)
  return rows.length > 0 && rows[0].period !== undefined
}

/** Every windowed panel holds all five windows; this keeps the selected one. */
export const panelRows = (
  panels: InsightsPanels,
  name: InsightsPanel,
  period: InsightsPeriod
): PanelRow[] => {
  const rows = rawPanel(panels, name)
  if (!isWindowed(panels, name)) return rows
  return rows.filter(row => row.period === period.key)
}

export const one = (
  panels: InsightsPanels,
  name: InsightsPanel,
  period: InsightsPeriod
): PanelRow => panelRows(panels, name, period)[0] ?? {}

const DAILY_PANELS: InsightsPanel[] = ['traffic_daily', 'users_daily']

/**
 * ONE ANCHOR DATE FOR THE WHOLE PAGE: the newest LOADED day in any daily panel.
 * Not today (the import lags), not per panel (two charts under one button
 * would cover different weeks), and not the last row (the date spine runs to
 * today with un-imported rows flagged is_loaded = false).
 */
export const anchorDay = (
  panels: InsightsPanels,
  period: InsightsPeriod
): string | null =>
  DAILY_PANELS.flatMap(name => panelRows(panels, name, period))
    .filter(row => row.is_loaded === true)
    .map(row => String(row.day))
    .sort()
    .pop() ?? null

const isoDay = (date: Date): string => date.toISOString().slice(0, 10)

const daysBefore = (day: string, count: number): string => {
  const from = new Date(`${day}T00:00:00Z`)
  from.setUTCDate(from.getUTCDate() - (count - 1))
  return isoDay(from)
}

export type Series = {
  rows: PanelRow[]
  xKey: 'day' | 'week'
  grain: 'day' | 'week'
}

/**
 * TWO GRAINS, PICKED BY WINDOW LENGTH. A week or a month is read day by day,
 * bounded by date at both ends; longer windows take the last N weekly rows.
 * "Last 7 days" cannot come off the weekly series (a partial Monday-start
 * bucket) and the daily series cannot be rolled up in the browser (distinct
 * people do not add). A missing daily panel falls back to weekly rather than
 * rendering an empty card.
 */
export const series = (
  panels: InsightsPanels,
  period: InsightsPeriod,
  weeklyName: InsightsPanel,
  dailyName: InsightsPanel
): Series => {
  const anchor = anchorDay(panels, period)
  if (period.days && anchor) {
    const daily = panelRows(panels, dailyName, period)
    if (daily.length) {
      const cutoff = daysBefore(anchor, period.days)
      return {
        rows: daily.filter(
          row => String(row.day) >= cutoff && String(row.day) <= anchor
        ),
        xKey: 'day',
        grain: 'day',
      }
    }
  }
  const weekly = panelRows(panels, weeklyName, period)
  return {
    rows: weekly.slice(-weeksFor(period)),
    xKey: 'week',
    grain: 'week',
  }
}

/**
 * How many weekly rows a window spans. A day-grained window that has to fall
 * back to weekly (daily panel missing) is bounded by its own length rather
 * than showing the whole history under a "last 7 days" label. All time takes
 * everything.
 */
const weeksFor = (period: InsightsPeriod): number => {
  if (period.weeks) return period.weeks
  if (period.days) return Math.ceil(period.days / 7)
  return Number.POSITIVE_INFINITY
}

const shortDate = (day: string): string =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })

/**
 * The range the window actually resolves to, for the picker's note. Derived
 * from the window, not from any one panel, so it is the same range every chart
 * shows — including one with no data in it.
 */
export const resolvedRange = (
  panels: InsightsPanels,
  period: InsightsPeriod
): string => {
  const anchor = anchorDay(panels, period)
  if (period.days && anchor) {
    return `${shortDate(daysBefore(anchor, period.days))} to ${shortDate(anchor)}`
  }
  const weeks = series(panels, period, 'traffic_weekly', 'traffic_daily').rows
  if (!weeks.length) return ''
  const first = String(weeks[0].week)
  const last = String(weeks[weeks.length - 1].week)
  return `weeks of ${shortDate(first)} to ${shortDate(last)}`
}

/** " · last 7 days", or nothing for all time — "Real users: 1" needs it. */
export const windowSuffix = (period: InsightsPeriod): string =>
  period.key === 'all' ? '' : ` · ${period.label.toLowerCase()}`

/** "ever" / "in this period" — a window claim must not read as absolute. */
export const inWindowPhrase = (period: InsightsPeriod): string =>
  period.key === 'all' ? 'ever' : 'in this period'
